import { EventEmitter, Injectable } from '@angular/core';
import { CPI } from '../../interfaces/cpi.interface';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  SurveyTopBarData,
  FeasibilityData,
  CPIData,
  SuppliersSelected,
  UpdateSupplierPayloadInterface,
  ProcessingData,
  SurveyData,
  SurveyPayload,
  CpiFeasibilityData,
  FeasibilityRequestMetaData,
} from '../../interfaces/survey-meta-data.interface';
import {
  BuyerSurveyResponse,
  SupplierSpecificInfo,
} from '../../interfaces/survey.interface';
import { FeasibilityExclusionData } from '../../interfaces/feasibility.interface';

@Injectable({
  providedIn: 'root',
})
export class SurveyMetaDataService {
  private readonly _defaultSurveyMetaData: SurveyTopBarData = {
    surveyId: 0,
    surveyStatus: 0,
    surveyLastSaved: 0,
    surveyFieldTime: 0,
    totalClicks: 0,
    expected_loi: 0,
    expected_ir: 0,
    incl_excl: 0,
    is_pure_price_rate_card_enable: false,
  };
  private _surveyMetaData$ = new BehaviorSubject<SurveyTopBarData>(
    this._defaultSurveyMetaData
  );

  private readonly _defaultSurveyData = {
    surveyId: 0,
    mcUniqueId: '',
    activeTrafficChannel: false,
  };
  private _surveyData$ = new BehaviorSubject<SurveyData>(
    this._defaultSurveyData
  );

  private _surveyFeasibilityMetaData$ = new BehaviorSubject<FeasibilityData>({
    loading: false,
  });

  private _surveyCPIMetaData$ = new BehaviorSubject<CPIData>({
    loading: false,
  });

  private _surveySuppliersSelectionMetaData$ =
    new BehaviorSubject<SuppliersSelected>({});

  private _feasibilityExclusionData =
    new BehaviorSubject<FeasibilityExclusionData>({
      survey_id: 0,
      survey_grouping: [],
      is_exclusion: false,
    });

  private _quotoMapperValidateData$ = new BehaviorSubject<{
    charCountExceedQuotas: string[];
    duplicatedBuyerQuotaIds: string[];
  }>({
    charCountExceedQuotas: [],
    duplicatedBuyerQuotaIds: [],
  });
  private _choosedSuppliersMetaData$ =
    new BehaviorSubject<UpdateSupplierPayloadInterface>(
      {} as UpdateSupplierPayloadInterface
    );
  private _supplierOverrideValue$ = new BehaviorSubject<boolean>(false);
  private _purePriceRateCardEnable$ = new BehaviorSubject<boolean>(false);
  private _globalPriceOverrideEnable$ = new BehaviorSubject<boolean>(false);
  private _processingSaveSurveyMetaData$ = new Subject();
  private _processingSurveyMetaData$ = new BehaviorSubject<ProcessingData>({});
  private _isFormDirty$ = new BehaviorSubject<boolean>(false);
  private _cpiDropdownMetaData$ = new BehaviorSubject<CPI[]>([]);
  private _supplierSpecificInfo$ = new BehaviorSubject<SupplierSpecificInfo>({
    eligibleSuppliers: [],
    supplierBlendData: [],
  });

  public readonly defaultCpiFeasibilityData: CpiFeasibilityData = {
    supplierSpecificInfo: {
      eligibleSuppliers: [],
      supplierBlendData: [],
    },
    cpiData: { loading: false },
    feasibilityData: { loading: false },
  };
  private _cpiFeasibilityData$ = new BehaviorSubject<CpiFeasibilityData>(
    this.defaultCpiFeasibilityData
  );

  private _feasibilityRequestMetaData$ = new BehaviorSubject(
    <FeasibilityRequestMetaData>{}
  );

  private _purePriceApplied$ = new BehaviorSubject<boolean>(false);

  private _buyerSurveyData$ = new BehaviorSubject<BuyerSurveyResponse>(
    {} as BuyerSurveyResponse
  );
  private _priceRecomendationCPI$ = new BehaviorSubject<number>(0);
  updateSurveyPayload = new EventEmitter();

  private _manuallyEditedAllocation$ = new BehaviorSubject<boolean>(false);

  private _manuallyEditedAllocationSaved$ = new BehaviorSubject<boolean>(false);

  private _supplierBlends$ = new BehaviorSubject<string>('');

  constructor() {}

  /**
   * Revert the survey meta data stores to their default state so we don't display previous survey info
   */
  clearAll() {
    this._surveyMetaData$.next(this._defaultSurveyMetaData);
    this._surveyFeasibilityMetaData$.next({ loading: false });
    this._surveyCPIMetaData$.next({ loading: false });
    this._surveyData$.next(this._defaultSurveyData);
  }

  getBlendsData(): Observable<string> {
    return this._supplierBlends$.asObservable();
  }

  setBlendsData(blendId: string) {
    this._supplierBlends$.next(blendId);
  }

  getSurveyHeaderData(): Observable<SurveyTopBarData> {
    return this._surveyMetaData$.asObservable();
  }

  sendSurveyHeaderData(topBarFields: SurveyTopBarData) {
    this._surveyMetaData$.next(topBarFields);
  }

  patchSurveyHeaderData(topBarFields: SurveyTopBarData) {
    this._surveyMetaData$.next({
      ...this._surveyMetaData$.value,
      ...topBarFields,
    });
  }

  getFeasibilityData(): Observable<FeasibilityData> {
    return this._surveyFeasibilityMetaData$.asObservable();
  }

  sendFeasibilityLoading() {
    this._surveyFeasibilityMetaData$.next({
      loading: true,
    });
  }

  sendFeasibilityData(feasibilityData: FeasibilityData) {
    this._surveyFeasibilityMetaData$.next(feasibilityData);
  }

  getCPIData(): Observable<CPIData> {
    return this._surveyCPIMetaData$.asObservable();
  }

  sendCPILoading() {
    this._surveyCPIMetaData$.next({ loading: true });
  }

  sendCPIData(cpiData: CPIData) {
    this._surveyCPIMetaData$.next(cpiData);
  }

  getSuppliersSelected(): Observable<SuppliersSelected> {
    return this._surveySuppliersSelectionMetaData$.asObservable();
  }

  sendSuppliersSelected(suppliersSelected: SuppliersSelected) {
    this._surveySuppliersSelectionMetaData$.next(suppliersSelected);
  }

  setQuotaMapperValidateData(payload: {
    charCountExceedQuotas: string[];
    duplicatedBuyerQuotaIds: string[];
  }) {
    this._quotoMapperValidateData$.next(payload);
  }

  getQuotaMapperValidateData() {
    return this._quotoMapperValidateData$.asObservable();
  }

  setSupplierListPayload(supplier: UpdateSupplierPayloadInterface) {
    this._choosedSuppliersMetaData$.next(supplier);
  }

  getSupplierListPayload(): Observable<UpdateSupplierPayloadInterface> {
    return this._choosedSuppliersMetaData$.asObservable();
  }

  setSupplierOverrideValue(newValueToOverrideSupplier: boolean) {
    this._supplierOverrideValue$.next(newValueToOverrideSupplier);
  }
  getSupplierOverrideValue() {
    return this._supplierOverrideValue$.asObservable();
  }
  setSurveyData(surveyData: SurveyData) {
    this._surveyData$.next(surveyData);
  }
  getSurveyData(): Observable<SurveyData> {
    return this._surveyData$.asObservable();
  }

  setSurveyProcessing(processing: ProcessingData) {
    this._processingSurveyMetaData$.next(processing);
  }

  getSurveyProcessing(): Observable<ProcessingData> {
    return this._processingSurveyMetaData$.asObservable();
  }

  sendCPIDropdownData(cpiData: CPI[]) {
    this._cpiDropdownMetaData$.next(cpiData);
  }

  getCPIDropdownData(): Observable<CPI[]> {
    return this._cpiDropdownMetaData$.asObservable();
  }

  sendSupplierSpecificInfo(data: SupplierSpecificInfo) {
    this._supplierSpecificInfo$.next(data);
  }

  getSupplierSpecificInfo(): Observable<SupplierSpecificInfo> {
    return this._supplierSpecificInfo$.asObservable();
  }

  sendCpiFeasibilityData(data: CpiFeasibilityData) {
    this._cpiFeasibilityData$.next(data);
  }

  getCpiFeasibilityData(): Observable<CpiFeasibilityData> {
    return this._cpiFeasibilityData$.asObservable();
  }

  sendFeasibilityRequestMetaData(data: FeasibilityRequestMetaData) {
    this._feasibilityRequestMetaData$.next(data);
  }

  getFeasibilityRequestMetaData(): Observable<FeasibilityRequestMetaData> {
    return this._feasibilityRequestMetaData$.asObservable();
  }

  getPurePriceApplied(): Observable<boolean> {
    return this._purePriceApplied$.asObservable();
  }

  applyPurePriceV2(flag: boolean) {
    this._purePriceApplied$.next(flag);
  }

  sendBuyerSurveyData(data: BuyerSurveyResponse) {
    this._buyerSurveyData$.next(data);
  }

  getBuyerSurveyData(): Observable<BuyerSurveyResponse> {
    return this._buyerSurveyData$.asObservable();
  }

  listenSaveSurvey(): Observable<unknown> {
    return this._processingSaveSurveyMetaData$.asObservable();
  }

  saveSurvey() {
    this._processingSaveSurveyMetaData$.next();
  }

  setIsFormDirty(flag: boolean) {
    this._isFormDirty$.next(flag);
  }

  getisFormDirty() {
    return this._isFormDirty$.asObservable();
  }

  sendSurveyPayload(data: SurveyPayload) {
    this.updateSurveyPayload.emit(data);
  }

  getFeasibilityExclusionData() {
    return this._feasibilityExclusionData.asObservable();
  }

  sendFeasibilityExclusionData(data: FeasibilityExclusionData) {
    this._feasibilityExclusionData.next(data);
  }

  getManuallyEditedAllocation() {
    return this._manuallyEditedAllocation$.asObservable();
  }

  updateManuallyEditedAllocationStatus(state: boolean) {
    this._manuallyEditedAllocation$.next(state);
  }

  isManuallyEditedAllocationSaved() {
    return this._manuallyEditedAllocationSaved$.asObservable();
  }

  updateManuallyEditedAllocationSaved(state: boolean) {
    this._manuallyEditedAllocationSaved$.next(state);
  }

  setPurePriceRateCardEnable(newPurePriceRateCardEnable: boolean) {
    this._purePriceRateCardEnable$.next(newPurePriceRateCardEnable);
  }
  getPurePriceRateCardEnable() {
    return this._purePriceRateCardEnable$.asObservable();
  }

  setShowPurepriceFlyout(
    globalOverride: boolean,
    selectivePriceOveride: boolean
  ) {
    this._globalPriceOverrideEnable$.next(
      globalOverride || selectivePriceOveride
    );
  }
  getShowPurepriceFlyout() {
    return this._globalPriceOverrideEnable$.asObservable();
  }
}
